import { useEffect, useRef } from 'react';
import { trackScrollEngagement } from '../utils/analytics';

const THRESHOLDS = [25, 50, 75, 90];

/**
 * Custom hook for tracking scroll engagement using react-ga4
 * Tracks when user scrolls 25%, 50%, 75%, and 90% of the page
 * @param enabled - Whether to enable scroll tracking (default: true)
 */
export const useScrollTracking = (enabled: boolean = true) => {
  // A ref, not state: this hook is called from _app, so setState here re-renders the whole
  // app tree. Past the first threshold every scroll frame called the setter, and React
  // re-renders a component once before it can bail out on an unchanged value — so the root
  // was re-rendering at 60fps while the user scrolled. Nothing on screen depends on which
  // thresholds have fired; it is bookkeeping to keep GA4 from counting one twice.
  const fired = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!enabled) return undefined;

    const handleScroll = () => {
      if (typeof window === 'undefined') return;

      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      // A page shorter than the viewport has nothing to scroll through — without this the
      // division is 0/0 and every threshold fires at once on the first wheel tick.
      if (scrollHeight <= 0) return;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollPercent = Math.round((scrollTop / scrollHeight) * 100);

      THRESHOLDS.forEach((threshold) => {
        if (scrollPercent >= threshold && !fired.current.has(threshold)) {
          fired.current.add(threshold);
          trackScrollEngagement(threshold);
        }
      });
    };

    // Throttle scroll events for performance
    let ticking = false;
    const throttledHandleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledHandleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
    };
  }, [enabled]);
};
